import { api } from '../axiosClient';
import type {
  AccountingPeriodsResponse,
  AccountingPeriod,
  CreateAccountingPeriodRequest,
  UpdateAccountingPeriodRequest,
  UpdateAccountingPeriodStatusRequest,
  AccountingPeriodConfiguration,
  AccountingPeriodConfigurationRequest,
  AccountingPeriodMetadata,
} from './accountingPeriod.types';
import { ACCOUNTING_PERIOD_BASE_PATH } from './constants';

export const getAccountingPeriodsByYear = (
  year: number,
): Promise<AccountingPeriodsResponse> => {
  return api.get(`${ACCOUNTING_PERIOD_BASE_PATH}/year/${year}`);
};

export const getAccountingPeriodsByYearAndMonth = (
  year: number,
  startMonth: number,
): Promise<AccountingPeriodsResponse> => {
  return api.get(
    `${ACCOUNTING_PERIOD_BASE_PATH}/year/${year}?startMonth=${startMonth}`,
  );
};

export const deleteAccountingPeriod = (id: number): Promise<void> => {
  return api.delete(`${ACCOUNTING_PERIOD_BASE_PATH}/${id}`);
};

export const createAccountingPeriod = (
  data: CreateAccountingPeriodRequest,
): Promise<AccountingPeriod> => {
  return api.post(`${ACCOUNTING_PERIOD_BASE_PATH}`, data);
};

export const getAccountingPeriodById = (
  id: number,
): Promise<AccountingPeriod> => {
  return api.get(`${ACCOUNTING_PERIOD_BASE_PATH}/${id}`);
};

export const updateAccountingPeriod = (
  id: number,
  data: UpdateAccountingPeriodRequest,
): Promise<AccountingPeriod> => {
  return api.patch(`${ACCOUNTING_PERIOD_BASE_PATH}/${id}`, data);
};

export const updateAccountingPeriodStatus = (
  id: number,
  data: UpdateAccountingPeriodStatusRequest,
): Promise<AccountingPeriod> => {
  return api.patch(`${ACCOUNTING_PERIOD_BASE_PATH}/${id}/status`, data);
};

export const getAccountingPeriodConfiguration =
  (): Promise<AccountingPeriodConfiguration> => {
    return api.get(`${ACCOUNTING_PERIOD_BASE_PATH}/configuration`);
  };

export const updateAccountingPeriodConfiguration = (
  data: AccountingPeriodConfigurationRequest,
): Promise<AccountingPeriodConfiguration> => {
  return api.patch(`${ACCOUNTING_PERIOD_BASE_PATH}/configuration`, data);
};

// Metadata API function
export const getAccountingPeriodMetadata =
  (): Promise<AccountingPeriodMetadata> => {
    return api.get(`${ACCOUNTING_PERIOD_BASE_PATH}/metadata`);
  };

// Automatic mode API function
export const executeAutomaticMode = (
  configuration?: AccountingPeriodConfiguration,
): Promise<void> => {
  // Convert configuration to API format
  const requestData = {
    accountingPeriodType: 0, // Default to Monthly
    fiscalStartMonth: 1, // Default to January
  };

  if (configuration) {
    // Convert period type to API format
    if (configuration.accountingPeriodType) {
      switch (configuration.accountingPeriodType) {
        case 'Month':
          requestData.accountingPeriodType = 0;
          break;
        case 'Quarter':
          requestData.accountingPeriodType = 1;
          break;
        case 'Year':
          requestData.accountingPeriodType = 2;
          break;
        default:
          requestData.accountingPeriodType = 0; // Monthly
      }
    }

    // Convert fiscal year start date to month
    if (configuration.fiscalYearStartDate) {
      try {
        const date = new Date(configuration.fiscalYearStartDate);
        requestData.fiscalStartMonth = date.getMonth() + 1; // Convert to 1-12 range
      } catch {
        requestData.fiscalStartMonth = 1; // Default to January
      }
    }
  }

  // Log the endpoint and request body for debugging
  const endpoint = `${ACCOUNTING_PERIOD_BASE_PATH}/automaticmode`;

  return api.post(endpoint, requestData, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
};
